import React, { ReactElement, useEffect } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import UiKit from '../../../../../shared/ui/ui-kit';
import { colorsPalette } from '../../../../../shared/ui/styles/colors-palette';
import { SubmittingStatus } from '../../../../../shared/infrastructure/types/common/common-types';
import { useTranslation } from 'react-i18next';
import { useUILogic } from '../../../shared/providers/UILogicProvider/hooks';

interface RecoveryResetFormProps {
  resetPasswordToken?: string;
  email?: string;
}

let RecoveryResetForm = function ({
  resetPasswordToken,
  email,
}: RecoveryResetFormProps): ReactElement {
  const { t } = useTranslation();
  const { authorizationUiModule } = useUILogic();
  const restoreUIManager = authorizationUiModule.restorePasswordUIManager;
  const coreManager = restoreUIManager.coreManager;
  const showValidation = coreManager.formStatus === SubmittingStatus.ERROR_SUBMITTING;

  useEffect(() => {
    coreManager.setResetParams(resetPasswordToken as string, email as LoginEmail);
  }, []);

  const passwordError =
    coreManager.passwordServerError ??
    (showValidation && !coreManager.validator.password.isValid
      ? t(coreManager.validator.password.errors[0])
      : undefined);
  const passwordConfirmationError =
    coreManager.passwordConfirmationServerError ??
    (showValidation && !coreManager.validator.passwordConfirmation.isValid
      ? t(coreManager.validator.passwordConfirmation.errors[0])
      : undefined);

  return (
    <Wrapper>
      <Header>
        <Heading>{t('recovery.reset.heading')}</Heading>
        <Description>
          {t('recovery.reset.description', {
            email: coreManager.email ?? '',
          })}
        </Description>
      </Header>
      {coreManager.globalError && <GlobalError>{t(coreManager.globalError)}</GlobalError>}
      <FieldsGroup>
        <UiKit.form.Item.VerticalLayoutTemplate label={t('recovery.reset.label.password')}>
          <UiKit.form.Validation
            validateStatus={passwordError ? 'error' : ''}
            message={passwordError as string}
            turnValidation={!!passwordError}
          >
            <UiKit.Input
              value={coreManager.newPassword}
              onChange={value => coreManager.setNewPassword(value as LoginPassword)}
              type="password"
              showPasswordVisibilityToggle={true}
            />
          </UiKit.form.Validation>
        </UiKit.form.Item.VerticalLayoutTemplate>
        <UiKit.form.Item.VerticalLayoutTemplate
          label={t('recovery.reset.label.passwordConfirmation')}
        >
          <UiKit.form.Validation
            validateStatus={passwordConfirmationError ? 'error' : ''}
            message={passwordConfirmationError as string}
            turnValidation={!!passwordConfirmationError}
          >
            <UiKit.Input
              value={coreManager.passwordConfirmation}
              onChange={value => coreManager.setPasswordConfirmation(value as LoginPassword)}
              type="password"
              showPasswordVisibilityToggle={true}
            />
          </UiKit.form.Validation>
        </UiKit.form.Item.VerticalLayoutTemplate>
      </FieldsGroup>
      <UiKit.Button
        loading={coreManager.formStatus === SubmittingStatus.SUBMITTING}
        customStyles={{ width: '100%' }}
        type="primary"
        onClick={() => coreManager.submitResetPassword()}
      >
        {t('recovery.reset.submit.button')}
      </UiKit.Button>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;
const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const Heading = styled.div`
  font-family: 'SkorozvonRoboto';
  font-weight: 500;
  font-size: 38px;
  line-height: 46px;
  color: ${colorsPalette.gray700};
  text-align: center;
`;
const Description = styled.div`
  font-size: 12px;
  line-height: 20px;
  color: ${colorsPalette.gray300};
  text-align: center;
  white-space: pre-line;
`;
const GlobalError = styled.div`
  font-size: 14px;
  color: ${colorsPalette.red600};
  text-align: center;
`;
const FieldsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

RecoveryResetForm = observer(RecoveryResetForm);

export { RecoveryResetForm };
